import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { Action, EmailConfirmationError, ThunkAction } from '../types';
import submitFormHack, { submitFormHackReplacement } from '../utilities/submit-form';
import { checkFeatureGate } from '../utilities/feature-flags/feature-gate';

import { State as ChangeEmailState } from '../reducers/change-email-reducer';

export type ChangeEmailAction =
  | { type: 'CHANGE_EMAIL_RESET_STATE' }
  | { type: 'CHANGE_EMAIL_REQUEST' }
  | { type: 'CHANGE_EMAIL_REQUEST_FAILED'; error: EmailConfirmationError }
  | { type: 'CHANGE_EMAIL_REQUEST_SUCCEEDED'; redirectTo: string }
  | { type: 'CHANGE_EMAIL_STATE_UPDATE'; backendState: Partial<ChangeEmailState> };

export const resetChangeEmailStateAction = (): Action => ({
  type: 'CHANGE_EMAIL_RESET_STATE',
});

export const changeEmailRequestAction = (): Action => ({
  type: 'CHANGE_EMAIL_REQUEST',
});

export const changeEmailRequestFailedAction = (error: EmailConfirmationError): Action => ({
  type: 'CHANGE_EMAIL_REQUEST_FAILED',
  error,
});
export const changeEmailRequestSucceededAction = (redirectTo: string): Action => ({
  type: 'CHANGE_EMAIL_REQUEST_SUCCEEDED',
  redirectTo,
});

export const changeEmailStateUpdateAction = (backendState: Partial<ChangeEmailState>): Action => ({
  type: 'CHANGE_EMAIL_STATE_UPDATE',
  backendState,
});

const CHANGE_EMAIL_REST_ENDPOINT = '/rest/verify-email/change-email';

export const changeEmailConfirmation =
  (email: string, password: string, token: string, userId: string, source: string): ThunkAction =>
  async (dispatch, getState) => {
    // Fork to use either the old submitFormHack or new submitFormHackReplacement function, well as the old or new endpoint
    // old endpoint: /verify-email/change-email (submitFormHack - form submission with input div manipulation)
    // new endpoint: /rest/verify-email/change-email (submitFormHackReplacement - REST POST request with JSON payload)
    if (!checkFeatureGate(AidFrontendFeatureGate.ENABLE_JSON_ENDPOINT_FOR_VERIFY_EMAIL)) {
      submitFormHack({
        method: 'POST',
        payload: { email, password },
        skipSanitizationKeys: ['password'],
      });

      return;
    }

    try {
      const csrfToken = getState().csrfToken;

      const res = await submitFormHackReplacement({
        method: 'POST',
        // These three parameters are included in the link from the verification email, therefore including to match existing functionality.
        action: `${CHANGE_EMAIL_REST_ENDPOINT}?token=${token}&userId=${userId}&source=${source}`,
        payload: { email, password },
        csrfToken,
      });

      // This indicates the backend is not behaving as expected
      // since even in error cases, we expect the backend endpoint to return a 200
      if (!res.ok) {
        dispatch(changeEmailRequestFailedAction('unknown_error'));
        return;
      }

      const response = await res.json();

      // We have state from the backend, which we want to ensure is updated in the FE state.
      if (response.changeEmail) {
        // Want to make sure the response is a valid object.
        const isChangeEmailObject =
          typeof response.changeEmail === 'object' && !Array.isArray(response.changeEmail);

        dispatch(changeEmailStateUpdateAction(isChangeEmailObject ? response.changeEmail : {}));
      }

      if (response?.changeEmail?.error) {
        const error = response.changeEmail.error;

        const validErrorTypes: EmailConfirmationError[] = [
          'invalid_token',
          'invalid_password',
          'unknown_error',
        ];

        if (!validErrorTypes.includes(error)) {
          dispatch(changeEmailRequestFailedAction('unknown_error'));
          return;
        }

        dispatch(changeEmailRequestFailedAction(error));
        return;
      }

      // The types on the FE define the response to always be under `changeEmail` for this endpoint
      // but in practice the BE returns in some cases (i.e. success) in a different format
      // so we want to handle that
      const redirectTo: string | undefined =
        response?.changeEmail?.redirectTo || response?.redirectTo;

      if (!redirectTo) {
        // If we don't get a redirect URL from the BE _and_ we haven't been able to understand the error
        // we don't know where to send the user and there aren't enough details of the error (or they aren't in the expected format) so we are assuming the request failed.

        dispatch(changeEmailRequestFailedAction('unknown_error'));
        return;
      }

      dispatch(changeEmailRequestSucceededAction(redirectTo));
      return;
    } catch (error) {
      dispatch(changeEmailRequestFailedAction('unknown_error'));
    }
  };
